import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSetting } from './useSetting'
import { getTickerBars } from '../api/marketData'
import { useIsMarketOpen } from '../lib/marketHours'
import {
  TREND_BLOCK_KEY,
  TREND_STRENGTH_KEY,
  computeTrend,
  loadTrendMAs,
  parseTrendStrength,
  type TrendDir,
} from '../lib/trendRule'

export interface TrendBlockStatus {
  trend: TrendDir | null
  /** Reason for blocking long-bias opens (long shares, BUY call). null when allowed. */
  longReason: string | null
  /** Reason for blocking short-bias opens (BUY put). null when allowed. */
  shortReason: string | null
}

/**
 * "Trade with the trend" rule. Computes intraday MA-stack alignment from the
 * EMAs/SMAs the user has enabled on their chart and blocks counter-trend
 * opens:
 *
 *  - Uptrend (fast > mid > slow on 1m closes) → BUY put blocked.
 *  - Downtrend (fast < mid < slow) → long shares + BUY call blocked.
 *  - Sideways / insufficient data → nothing blocked.
 *
 * Closes are never affected by this hook — it only feeds the BUY-disabled path.
 */
export function useTrendBlock(symbol: string | null | undefined): TrendBlockStatus {
  const [enabled] = useSetting<boolean>(TREND_BLOCK_KEY, false)
  const [strengthRaw] = useSetting<unknown>(TREND_STRENGTH_KEY, null)
  const marketOpen = useIsMarketOpen()

  const barsQ = useQuery({
    queryKey: ['trend-bars', symbol],
    queryFn: () => getTickerBars(symbol!, '1D', '1m'),
    enabled: !!enabled && !!symbol,
    staleTime: 30_000,
    refetchInterval: enabled && marketOpen ? 30_000 : false,
    refetchIntervalInBackground: false,
    retry: 1,
  })

  // Reading from localStorage on every render is cheap. Tying the memo to
  // the bars data also makes the trend recompute whenever new bars arrive,
  // which is what we want.
  const trend = useMemo<TrendDir | null>(() => {
    if (!enabled) return null
    const bars = barsQ.data?.bars
    if (!bars || bars.length === 0) return null
    return computeTrend(bars, loadTrendMAs(), parseTrendStrength(strengthRaw))
  }, [enabled, barsQ.data, strengthRaw])

  if (!enabled || trend == null) {
    return { trend: null, longReason: null, shortReason: null }
  }

  if (trend === 'up') {
    return { trend, longReason: null, shortReason: 'Counter-trend (intraday uptrend)' }
  }
  if (trend === 'down') {
    return { trend, longReason: 'Counter-trend (intraday downtrend)', shortReason: null }
  }
  return { trend, longReason: null, shortReason: null }
}
